import * as graphql from 'generated/graphql';

export default function useViewerRoles(): {
	loading: boolean;
	roles: graphql.UserRole[];
	isManager: boolean;
	isDepartmentManager: boolean;
	isTeamManager: boolean;
	isGroupManager: boolean;
	isLearningOfficer: boolean;
	isEngagementOfficer: boolean;
	isWorkspaceSupervisor: boolean;
	isWorkspaceSuperuser: boolean;
	isWorkspaceAdmin: boolean;
} {
	const { data, loading } = graphql.useViewerRolesQuery({
		fetchPolicy: 'cache-first',
	});

	function hasRoleID(id: string) {
		return data?.viewer.roles.some((role) => role.role.id === id) ?? false;
	}

	function hasRoleType(type: graphql.RoleType) {
		return data?.viewer.roles.some((role) => role.role.type === type) ?? false;
	}

	function hasRoleTargetType(targetType: graphql.RoleTargetType, type: graphql.RoleType) {
		return (
			data?.viewer.roles.some(
				(role) => role.target.type === targetType && role.role.type === type,
			) ?? false
		);
	}

	const isWorkspaceSuperuser = hasRoleID('workspace-super-user');
	const isLearningOfficer = hasRoleID('workspace-learning-officer');
	const isEngagementOfficer = hasRoleID('workspace-engagement-officer');
	const isWorkspaceSupervisor = hasRoleID('workspace-supervisor');
	const isWorkspaceAdmin = hasRoleID('workspace-admin');

	const isManager = [
		hasRoleType(graphql.RoleType.PrimaryManager),
		hasRoleType(graphql.RoleType.CollaborativeManager),
		hasRoleType(graphql.RoleType.Coach),
	].some(Boolean);

	const isDepartmentManager = [
		hasRoleTargetType(graphql.RoleTargetType.Department, graphql.RoleType.PrimaryManager),
		hasRoleTargetType(graphql.RoleTargetType.Department, graphql.RoleType.CollaborativeManager),
		hasRoleTargetType(graphql.RoleTargetType.Department, graphql.RoleType.Coach),
	].some(Boolean);

	const isTeamManager = hasRoleTargetType(graphql.RoleTargetType.Team, graphql.RoleType.Owner);

	const isGroupManager = isWorkspaceSuperuser || isDepartmentManager || isTeamManager;

	return {
		loading,
		roles: (data?.viewer.roles as graphql.UserRole[]) ?? [],
		isManager,
		isDepartmentManager,
		isTeamManager,
		isGroupManager,
		isLearningOfficer,
		isEngagementOfficer,
		isWorkspaceSupervisor,
		isWorkspaceSuperuser,
		isWorkspaceAdmin,
	};
}
